import { useState } from 'react'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import type { PlanCode, PlanProps } from '@server/domain/billing'

import CustomAvatar from '@core/components/mui/avatar'
import CustomChip from '@core/components/mui/chip'
import type { ThemeColor } from '@core/types'
import CheckoutDialog from '@components/due-diligence/checkout-dialog'

import { useTRPC } from '@/lib/trpc'
import { formatBrl } from '@/lib/due-diligence'

export const Route = createFileRoute('/_dashboard/plans')({
  component: Plans
})

/**
 * What each plan sells, beyond the numbers the server holds. Price and quota
 * are data; this is the commercial promise, and it lives next to the screen.
 */
const PRESENTATION: Record<
  PlanCode,
  {
    audience: string
    icon: string
    color: ThemeColor
    featured?: boolean
    badge?: string
    benefits: string[]
    priceOverride?: string
    ctaOverride?: string
  }
> = {
  payg: {
    audience: 'Arrematante de leilão, uso eventual',
    icon: 'tabler-shopping-bag',
    color: 'secondary',
    priceOverride: 'sob demanda',
    ctaOverride: 'Comprar avulso',
    benefits: ['Relatório PDF por dossiê', 'Score de risco com parecer', 'Pix ou cartão, sem assinatura']
  },
  starter: {
    audience: 'Corretor autônomo, segurador',
    icon: 'tabler-bolt',
    color: 'info',
    benefits: ['Relatório PDF com score', 'Histórico de 90 dias', '1 usuário · suporte por e-mail']
  },
  pro: {
    audience: 'Corretor especializado, advogado solo',
    icon: 'tabler-sparkles',
    color: 'primary',
    featured: true,
    badge: 'Mais vendido',
    benefits: [
      'Dossiês de PF, PJ e imóvel',
      'Relatório com sua marca',
      '2 usuários · histórico ilimitado',
      'API básica (100 chamadas/mês)'
    ]
  },
  business: {
    audience: 'Imobiliária, escritório médio',
    icon: 'tabler-building-skyscraper',
    color: 'warning',
    benefits: [
      'Todos os dossiês e combos',
      'Dashboard de operações',
      'Integração com CRMs',
      'API completa · suporte no WhatsApp'
    ]
  },
  enterprise: {
    audience: 'Grandes redes e escritórios AAA',
    icon: 'tabler-crown',
    color: 'error',
    priceOverride: 'sob consulta',
    ctaOverride: 'Falar com vendas',
    benefits: [
      'API dedicada e webhooks',
      'Integração customizada',
      'SLA 99,9% + gerente de conta',
      'DPA e documentação LGPD'
    ]
  }
}

function Plans() {
  const trpc = useTRPC()

  const { data: plans = [] } = useQuery(trpc.billing.plans.queryOptions())
  const { data: subscription } = useQuery(trpc.billing.mySubscription.queryOptions())

  const [checkout, setCheckout] = useState<PlanProps | null>(null)

  const currentPlan = subscription?.subscription.planCode

  const choose = (plan: PlanProps) => {
    if (plan.code === 'enterprise') {
      window.location.href = 'mailto:comercial@bravaforte.com.br?subject=Plano Enterprise — Due Diligence'

      return
    }

    setCheckout(plan)
  }

  const comparison = plans.filter(plan => ['starter', 'pro', 'business'].includes(plan.code))

  return (
    <>
      <div className='text-center mbe-10'>
        <CustomChip round='true' variant='tonal' color='primary' label='Escolha o plano ideal' className='mbe-4' />
        <Typography variant='h3'>Due diligence imobiliária sob medida</Typography>
        <Typography color='text.secondary' className='mbs-2 mli-auto max-is-[620px]'>
          Do arrematante de leilão à grande rede: dossiês de pessoas, empresas e imóveis, com score de risco e parecer
          jurídico gerado por IA.
        </Typography>
      </div>

      {/* How the monthly limit works: an analysis contains dossiers */}
      <Card className='mbe-10'>
        <CardContent>
          <div className='text-center mbe-6'>
            <Typography variant='overline' color='primary.main'>
              Estrutura: análise × dossiês
            </Typography>
            <Typography variant='h5'>Como funciona o limite mensal</Typography>
            <Typography variant='body2' color='text.secondary'>
              Cada análise inclui vários dossiês. Passou do limite, o extra tem preço fixo.
            </Typography>
          </div>

          <Grid container spacing={6}>
            {comparison.map(plan => {
              const info = PRESENTATION[plan.code as PlanCode]

              return (
                <Grid key={plan.code} size={{ xs: 12, md: 4 }}>
                  <div
                    className='rounded border plb-5 pli-5 bs-full'
                    style={info.featured ? { borderColor: 'var(--mui-palette-primary-main)' } : undefined}
                  >
                    <div className='flex items-center gap-3 mbe-4'>
                      <CustomAvatar skin='light' color={info.color} variant='rounded' size={36}>
                        <i className={`${info.icon} text-[20px]`} />
                      </CustomAvatar>
                      <Typography variant='h6'>{plan.name}</Typography>
                    </div>
                    <Typography variant='h4' className='mbe-4'>
                      {formatBrl(plan.monthlyPriceBrl)}
                      <Typography component='span' variant='body2' color='text.disabled'>
                        {' '}
                        /mês
                      </Typography>
                    </Typography>
                    <Grid container spacing={4}>
                      <Grid size={6}>
                        <div className='rounded bg-actionHover plb-3 pli-3'>
                          <Typography variant='caption' color='text.disabled'>
                            Análises
                          </Typography>
                          <Typography variant='h6' color={`${info.color}.main`}>
                            {plan.monthlyAnalyses}
                          </Typography>
                        </div>
                      </Grid>
                      <Grid size={6}>
                        <div className='rounded bg-actionHover plb-3 pli-3'>
                          <Typography variant='caption' color='text.disabled'>
                            Dossiês
                          </Typography>
                          <Typography variant='h6' color={`${info.color}.main`}>
                            {plan.monthlyDossiers}
                          </Typography>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* The five plans */}
      <Grid container spacing={6} alignItems='stretch'>
        {plans.map(plan => {
          const info = PRESENTATION[plan.code as PlanCode]
          const current = currentPlan === plan.code
          const unlimited = plan.monthlyAnalyses === -1

          return (
            <Grid key={plan.code} size={{ xs: 12, sm: 6, lg: 12 / 5 }}>
              <Card
                className='bs-full flex flex-col'
                sx={
                  info.featured
                    ? {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                        borderStyle: 'solid',
                        boxShadow: 'var(--mui-customShadows-lg)'
                      }
                    : undefined
                }
              >
                <CardContent className='flex flex-col gap-4 bs-full'>
                  <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-3'>
                      <CustomAvatar skin='light' color={info.color} variant='rounded' size={36}>
                        <i className={`${info.icon} text-[20px]`} />
                      </CustomAvatar>
                      <Typography variant='h6'>{plan.name}</Typography>
                    </div>
                    {info.badge && <CustomChip round='true' size='small' variant='tonal' color='primary' label={info.badge} />}
                    {current && <CustomChip round='true' size='small' variant='tonal' color='success' label='Atual' />}
                  </div>

                  <div>
                    <Typography variant='h5'>
                      {info.priceOverride ?? formatBrl(plan.monthlyPriceBrl)}
                      {!info.priceOverride && (
                        <Typography component='span' variant='body2' color='text.disabled'>
                          {' '}
                          /mês
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant='caption' color='text.disabled' className='italic'>
                      {info.audience}
                    </Typography>
                  </div>

                  <div className='rounded bg-actionHover plb-3 pli-3 flex flex-col gap-2'>
                    <div className='flex items-center justify-between gap-2'>
                      <Typography variant='body2' color='text.secondary'>
                        Análises/mês
                      </Typography>
                      <Typography variant='body2' className='font-medium'>
                        {unlimited ? 'Ilimitadas' : plan.monthlyAnalyses || '—'}
                      </Typography>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <Typography variant='body2' color='text.secondary'>
                        Dossiês/mês
                      </Typography>
                      <Typography variant='body2' className='font-medium'>
                        {unlimited ? 'Ilimitados' : plan.monthlyDossiers || '—'}
                      </Typography>
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                      <Typography variant='body2' color='text.secondary'>
                        Dossiê extra
                      </Typography>
                      <Typography variant='body2' className='font-medium'>
                        {plan.extraDossierPriceBrl ? formatBrl(plan.extraDossierPriceBrl) : '—'}
                      </Typography>
                    </div>
                  </div>

                  <div className='flex flex-col gap-2 flex-1'>
                    {info.benefits.map(benefit => (
                      <div key={benefit} className='flex items-start gap-2'>
                        <i className={`tabler-circle-check text-base text-${info.color}`} />
                        <Typography variant='body2' color='text.secondary'>
                          {benefit}
                        </Typography>
                      </div>
                    ))}
                  </div>

                  <Button
                    fullWidth
                    variant={info.featured ? 'contained' : 'tonal'}
                    color={current ? 'success' : info.color}
                    disabled={current}
                    onClick={() => choose(plan)}
                  >
                    {current ? 'Seu plano atual' : (info.ctaOverride ?? `Contratar ${plan.name}`)}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Divider className='mlb-10' />

      <Typography variant='body2' color='text.disabled' className='text-center'>
        Precisa de algo entre os planos? Fale com o time comercial e montamos uma proposta sob medida.
      </Typography>

      <CheckoutDialog plan={checkout} onClose={() => setCheckout(null)} />
    </>
  )
}
